import type { components } from '@/shared/api/schema'
import { paymentReadPermissions } from './paymentForm'
import { paymentAmount } from './paymentForm'

export const refundBase = '/api/v1/finance/supplier-refunds'
export const refundReadPermissions = [...paymentReadPermissions, 'finance.view_supplierrefund']
export type Refund = components['schemas']['RefundDocumentOutput']
export type RefundResult = components['schemas']['RefundActionOutput']
export type RefundAction = 'submit' | 'withdraw' | 'cancel' | 'failed' | 'approve' | 'reject' | 'receive'
export type RefundPending = { path: string; body: string; key: string; supplierId?: number }
export type RefundCreate = components['schemas']['RefundCreateInput']
export type RefundSource = components['schemas']['RefundSourceOutput']
export type RefundSourcePage = components['schemas']['RefundSourcePage']
export type RefundPage = components['schemas']['RefundPage']
export type RefundContext = components['schemas']['RefundReviewContext']
export type RefundProof = components['schemas']['RefundProofOutput']
export type RefundReceive = components['schemas']['RefundReceiptInput']
export type UploadIntent = { reason: string; name: string; size: number; type: string; sha256: string }
export const refundStates: Record<string, [string, string]> = {
  draft: ['草稿', 'Draft'], pending_approval: ['待审批', 'Pending approval'],
  awaiting_receipt: ['已批准待到账', 'Approved, awaiting receipt'], received: ['已到账', 'Received'],
  cancelled: ['已取消', 'Cancelled'], failed: ['退款失败', 'Failed'],
}
const integer = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value > 0
export const refundEventLabels: Record<string, [string, string]> = {
  submit: ['提交', 'Submit'], approve: ['通过', 'Approve'], reject: ['驳回', 'Reject'],
  withdraw: ['撤回', 'Withdraw'], cancel: ['取消', 'Cancel'], failed: ['退款失败', 'Refund failed'], receive: ['实际到账', 'Actual receipt'],
}
const money = (value: unknown): value is string => typeof value === 'string' && /^\d{1,14}\.\d{4}$/.test(value)
export function refundId(value: string): number | null {
  return /^[1-9]\d*$/.test(value) && integer(Number(value)) ? Number(value) : null
}
export function validateRefund(value: Refund, expectedId: number): Refund {
  if (!value || !integer(value.id) || value.id !== expectedId || !integer(value.supplier_id) || !integer(value.source_payment_id)
    || !integer(value.version) || !money(value.amount) || !Object.hasOwn(refundStates, value.status)
    || typeof value.doc_no !== 'string' || !value.doc_no || typeof value.reason !== 'string'
    || !['supplier_credit_note', 'supplier_prepayment'].includes(value.source_kind)
    || (value.source_kind === 'supplier_credit_note' ? !integer(value.credit_note_id) : value.credit_note_id !== null)
    || !Array.isArray(value.paid_sources) || value.paid_sources.some(row => !integer(row.portion_id) || !money(row.amount))
    || value.held_amount !== null && !money(value.held_amount)
    || value.approval_id !== null && !integer(value.approval_id)
    || typeof value.review_available !== 'boolean' || typeof value.execution_available !== 'boolean'
    || typeof value.reversal_available !== 'boolean' || typeof value.execution_blocker !== 'string'
    || typeof value.request_date !== 'string') throw new Error('refund contract')
  const cash = value.cash_receipt
  if (cash && (!integer(cash.id) || !integer(cash.evidence_id) || !money(cash.amount)
    || cash.amount !== value.amount || typeof cash.received_at !== 'string' || !Number.isFinite(Date.parse(cash.received_at))
    || typeof cash.method !== 'string' || typeof cash.reference_no !== 'string' || value.status !== 'received')) throw new Error('cash contract')
  return value
}
export function refundActionPermission(action: RefundAction): string {
  if (action === 'approve' || action === 'reject') return 'finance.review_supplierrefund'
  if (action === 'receive') return 'finance.receive_supplierrefund'
  return `finance.${['submit', 'withdraw'].includes(action) ? 'submit' : 'close'}_supplierrefund`
}
export function canRefundAction(value: Refund, action: RefundAction, can: (permission: string) => boolean, context?: RefundContext | null): boolean {
  if (!can(refundActionPermission(action)) || !value.review_available) return false
  if (action === 'approve' || action === 'reject') return Boolean(context && context.document.id === value.id
    && context.document.version === value.version && context.approval_id === value.approval_id
    && can('workflow.approve_approvalinstance') && (action === 'approve' ? context.can_approve : context.can_reject))
  if (action === 'receive') return value.status === 'awaiting_receipt' && value.execution_available
  return action === 'submit' ? value.status === 'draft'
    : action === 'withdraw' ? value.status === 'pending_approval' && integer(value.approval_id)
      && Boolean(context?.can_withdraw && context.document.id === value.id && context.document.version === value.version
        && context.approval_id === value.approval_id)
      : action === 'failed' ? value.status === 'awaiting_receipt'
        : ['draft', 'awaiting_receipt'].includes(value.status)
}
export function parseRefundPending(raw: string): RefundPending {
  const value = JSON.parse(raw) as RefundPending
  const create = value?.path === refundBase
  const upload = typeof value?.path === 'string' && /^\/api\/v1\/finance\/supplier-refunds\/[1-9]\d*\/evidence$/.test(value.path)
  if (!value || Object.keys(value).sort().join(',') !== (create ? 'body,key,path,supplierId' : 'body,key,path')
    || typeof value.path !== 'string' || !create && (!/^\/api\/v1\/finance\/supplier-refunds\/[1-9]\d*\/(submit|withdraw|cancel|failed|approve|reject|receive|evidence)$/.test(value.path)
      || !refundId(value.path.split('/')[5]!)) || create && !integer(value.supplierId)
    || typeof value.body !== 'string' || value.body.length > 15000
    || typeof value.key !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.key)) throw new Error('refund intent')
  const body = JSON.parse(value.body) as Record<string, unknown>
  if (upload) {
    if (!body || Object.keys(body).sort().join(',') !== 'name,reason,sha256,size,type'
      || typeof body.reason !== 'string' || !body.reason.trim() || body.reason.length > 256
      || typeof body.name !== 'string' || !body.name || body.name.length > 255
      || typeof body.type !== 'string' || !['application/pdf', 'image/png', 'image/jpeg'].includes(body.type)
      || !integer(body.size) || body.size > (body.type === 'application/pdf' ? 10 : 5) * 1024 * 1024
      || typeof body.sha256 !== 'string' || !/^[a-f0-9]{64}$/.test(body.sha256)) throw new Error('refund proof intent')
    return value
  }
  if (create) {
    const credit = body.source_kind === 'supplier_credit_note'
    if (!body || !['supplier_credit_note', 'supplier_prepayment'].includes(String(body.source_kind))
      || Object.keys(body).sort().join(',') !== (credit ? 'amount,credit_note_id,reason,request_date,source_kind,source_payment_id'
        : 'amount,reason,request_date,source_kind,source_payment_id')
      || !integer(body.source_payment_id) || credit && !integer(body.credit_note_id)
      || typeof body.amount !== 'string' || paymentAmount(body.amount) <= 0n
      || typeof body.request_date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(body.request_date)
      || typeof body.reason !== 'string' || !body.reason.trim() || body.reason.length > 2000) throw new Error('refund create intent')
    return value
  }
  const review = /\/(withdraw|approve|reject)$/.test(value.path), receive = value.path.endsWith('/receive')
  if (!body || Object.keys(body).sort().join(',') !== (review ? 'approval_id,expected_version,reason' : receive
    ? 'actual_received_confirmed,evidence_id,expected_version,method,reason,received_at,reference_no' : 'expected_version,reason')
    || !integer(body.expected_version) || body.expected_version > 2 ** 31 - 1
    || typeof body.reason !== 'string' || !body.reason.trim() || body.reason.length > 2000
    || review && !integer(body.approval_id)) throw new Error('refund body')
  if (receive && (!integer(body.evidence_id) || body.actual_received_confirmed !== true
    || typeof body.received_at !== 'string' || !Number.isFinite(Date.parse(body.received_at))
    || typeof body.method !== 'string' || !body.method.trim() || body.method.length > 64
    || typeof body.reference_no !== 'string' || !body.reference_no.trim() || body.reference_no.length > 128)) throw new Error('refund receive body')
  return value
}
export function refundPendingAllowed(value: RefundPending, can: (permission: string) => boolean): boolean {
  if (value.path === refundBase) return can('finance.add_supplierrefund')
  if (value.path.endsWith('/evidence')) return can('finance.receive_supplierrefund')
  const action = value.path.split('/').at(-1) as RefundAction
  return can(refundActionPermission(action)) && (!['approve', 'reject'].includes(action) || can('workflow.approve_approvalinstance'))
}
export function validateRefundContext(value: RefundContext, id: number): RefundContext {
  validateRefund(value?.document, id)
  if (!value || value.approval_id !== value.document.approval_id || typeof value.source_payment_no !== 'string'
    || !money(value.original_paid_amount) || !Number.isFinite(Date.parse(value.original_paid_at))
    || !['can_approve', 'can_reject', 'can_withdraw'].every(key => typeof value[key as keyof RefundContext] === 'boolean')
    || !Array.isArray(value.nodes) || value.nodes.some(node => !integer(node.sequence) || typeof node.name_zh !== 'string' || typeof node.name_en !== 'string')
    || !Number.isSafeInteger(value.count) || value.count < 0 || !Array.isArray(value.results)
    || value.results.some(row => !integer(row.id) || !integer(row.version) || row.version > value.document.version
      || !['submit', 'approve', 'reject', 'withdraw', 'cancel', 'failed', 'receive'].includes(row.action)
      || !Object.hasOwn(refundStates, row.status_after) || typeof row.reason !== 'string'
      || !Number.isFinite(Date.parse(row.occurred_at)) || typeof row.actor_name !== 'string')
    || (value.can_approve || value.can_reject || value.can_withdraw) && (value.document.status !== 'pending_approval'
      || value.approval_status !== 'pending' || !integer(value.approval_id) || !value.current_node
      || !value.nodes.some(node => node.sequence === value.current_node?.sequence))) throw new Error('refund review context')
  return value
}
export function validateRefundProof(value: RefundProof, id: number): RefundProof {
  if (!value || !integer(value.id) || value.refund_id !== id || typeof value.public_id !== 'string'
    || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.public_id)
    || typeof value.original_name !== 'string' || !value.original_name || !integer(value.size_bytes)
    || !['application/pdf', 'image/png', 'image/jpeg'].includes(value.mime_type)
    || !integer(value.uploaded_by) || !Number.isFinite(Date.parse(value.uploaded_at))) throw new Error('refund proof')
  return value
}
export async function refundFileIntent(file: File, reason: string): Promise<UploadIntent> {
  if (!file.name || file.name.length > 255 || !['application/pdf', 'image/png', 'image/jpeg'].includes(file.type)
    || file.size <= 0 || file.size > (file.type === 'application/pdf' ? 10 : 5) * 1024 * 1024) throw new Error('refund file')
  const sha256 = [...new Uint8Array(await crypto.subtle.digest('SHA-256', await file.arrayBuffer()))]
    .map(byte => byte.toString(16).padStart(2, '0')).join('')
  return { name: file.name, size: file.size, type: file.type, reason, sha256 }
}
export function validateRefundResult(value: RefundResult | components['schemas']['RefundCreateOutput'], pending: RefundPending): Refund {
  if (pending.path === refundBase) {
    const body = JSON.parse(pending.body) as RefundCreate
    const document = validateRefund(value?.document, value?.document?.id)
    if (typeof value.replayed !== 'boolean' || document.supplier_id !== pending.supplierId
      || document.source_payment_id !== body.source_payment_id || document.source_kind !== body.source_kind
      || document.credit_note_id !== (body.credit_note_id ?? null)
      || paymentAmount(document.amount) !== paymentAmount(body.amount)) throw new Error('refund create result')
    return document
  }
  const body = JSON.parse(pending.body) as { expected_version: number }
  const document = validateRefund(value?.document, Number(pending.path.split('/')[5]))
  if (typeof value.replayed !== 'boolean' || !('receipt_version' in value) || value.receipt_version !== body.expected_version + 1
    || document.version < value.receipt_version) throw new Error('refund result')
  if (pending.path.endsWith('/receive')) {
    const received = JSON.parse(pending.body) as RefundReceive, cash = document.cash_receipt
    if (!cash || cash.evidence_id !== received.evidence_id || cash.method !== received.method
      || cash.reference_no !== received.reference_no || Date.parse(cash.received_at) !== Date.parse(received.received_at)) throw new Error('refund receipt result')
  }
  return document
}
export function validateRefundPage(value: RefundPage, supplier: number): RefundPage {
  if (!value || value.scope_basis !== 'currently_authorized_sources' || !Number.isSafeInteger(value.count)
    || value.count < 0 || !Array.isArray(value.results)) throw new Error('refund page')
  value.results.forEach(row => { validateRefund(row, row.id); if (row.supplier_id !== supplier) throw new Error('refund supplier') })
  return value
}
export function validateRefundSources(value: RefundSourcePage, supplier: number, kind: string, credit: number | null): RefundSourcePage {
  if (!value || value.scope_basis !== 'currently_authorized_sources' || !Number.isSafeInteger(value.count)
    || value.count < 0 || !Array.isArray(value.results) || value.results.some(row => row.supplier_id !== supplier
      || row.source_kind !== kind || row.credit_note_id !== credit || !integer(row.source_payment_id)
      || typeof row.source_payment_no !== 'string' || !integer(row.payment_version)
      || ![row.original_source_paid, row.unallocated_available, row.maximum_draft_amount, row.proposed_amount].every(money)
      || row.credit_available !== null && !money(row.credit_available)
      || !Array.isArray(row.paid_sources) || row.paid_sources.some(piece => !integer(piece.portion_id) || !money(piece.amount))
      || typeof row.execution_available !== 'boolean' || typeof row.execution_blocker !== 'string')) throw new Error('refund sources')
  return value
}
